import { EAgentToolType } from "../enums/agent-tool-type";

export interface ICrewAgentTask {
  description: string;
  expected_output: string;
  output_format: {
    [key: string]: string;
  };
  // Task-level parameters for crewAI (directly on task object)
  // Guardrail to validate task output:
  // - Function-based: Python function name or code that returns (bool, result|error)
  // - LLM-based: Natural language description (e.g., "Make sure output is relevant to Ankara, Turkey")
  guardrail?: string;
  // Maximum retries if guardrail validation fails (matches backend: guardrailMaxRetryLimit)
  guardrailMaxRetryLimit?: number;
  // Whether to format output as Markdown
  markdown?: boolean;
  // Whether to request human input before finalizing
  human_input?: boolean;
  // Whether to execute this task asynchronously
  async_execution?: boolean;
  // definitions?: {
  //   [key: string]: {
  //     [key: string]: string;
  //   };
  // };
}

export interface ICrewCustomDataType {
  type_name: string;
  properties: {
    [key: string]: string;
  };
}

export interface ICrewAgentTool {
  name: string;
  mcpConfigName?:
    | "braveSearchSettings"
    | "slackSettings"
    | "dropboxSettings"
    | "githubSettings"
    | "googleMcpSettings"
    | "googleMapMcpSettings"
    | "jiraMcpSettings"
    | "youtubeMcpSettings"
    | "trelloMcpSettings"
    | "office365McpSettings"
    | "firecrawlMcpSettings"
    | "apifySettings";
  settingsId?: string;
  // KOA-16736: KAction tool support — matches Backend CrewAiAgentTool.cs
  toolType?: EAgentToolType;
  actionId?: string;
  description?: string;
}

// Matches Backend/Kuika.Designtime.Model/AiAgentModel/ECrewAiAgentMemoryMode.cs
export enum ECrewAiAgentMemoryMode {
  Disabled = 0,
  Shared = 1,
  Isolated = 2
}

// Matches Backend/Kuika.Designtime.Model/AiAgentModel/CrewAiModelConfig.cs
export interface ICrewAiModelConfig {
  id?: string;
  name?: string;
  providerId?: string;
}

// Matches Backend/Kuika.Designtime.Model/AiAgentModel/CrewAiMemoryOptions.cs
// Only `embedder` is user-configurable from the designer today; the other fields
// (weights, thresholds, llm) use backend defaults unless explicitly overridden.
export interface ICrewMemoryOptions {
  embedder?: ICrewAiModelConfig;
}

export interface ICrewAgent {
  name: string;
  role: string;
  goal: string;
  backstory: string;
  // Task is required in sequential mode and for the manager in hierarchical mode.
  // In hierarchical mode, sub-agents MUST NOT have a task -- the manager owns it and
  // delegates at runtime. Enforcement is runtime (CoreApi validator + codegen), not
  // at the type level, to avoid splitting this interface per process type.
  task?: ICrewAgentTask;
  tools?: ICrewAgentTool[];
  // LLM model ID (references AIModels.json Id) to use for this agent
  model?: string;
  // Optional separate LLM model ID for function/tool calling. If undefined, falls back to `model`.
  functionCallingLlm?: string;
  // Memory mode for this agent (Disabled / Shared crew memory / Isolated per-agent store).
  // In hierarchical mode this is set ONLY on the manager; sub-agents leave it undefined.
  memoryMode?: ECrewAiAgentMemoryMode;
  // Per-agent memory options. Only populated when memoryMode === Isolated -- the
  // embedder powering this agent's private vector store. Shared-mode agents draw
  // their embedder from the crew-level `memoryOptions` on ICrewAgentDTO instead.
  memoryOptions?: ICrewMemoryOptions;
  // Enable reasoning (chain-of-thought) for this agent. Defaults to false.
  reasoning?: boolean;
}

export interface ICrewInput {
  parameter_name: string;
  parameter_format: string;
  parameter_default_value?: string;
  description?: string;
}

// Hierarchical-mode manager agent. Owns the crew's single task, crew inputs, custom
// data types (output format), guardrails, and crew memory. Sub-agents in `crewAgents`
// are pure capability profiles (name/role/goal/backstory/model/tools).
//
// Fields are kept optional in TypeScript so existing construction sites (that only
// had {name, model, memoryMode} under the old model) still compile. The invariant
// "in hierarchical mode, the manager must have name/role/goal/backstory/task" is
// enforced at save time by the CoreApi validator, not by the type system.
export interface ICrewManagerAgent {
  name?: string;
  role?: string;
  goal?: string;
  backstory?: string;
  // The ONLY task in a hierarchical crew -- describes the overall workflow and
  // the final expected output. Manager decomposes and delegates at runtime.
  task?: ICrewAgentTask;
  tools?: ICrewAgentTool[];
  // LLM model ID (references AIModels.json Id) to use for this manager agent
  model?: string;
  // Optional separate LLM model ID for function/tool calling. If undefined, falls back to `model`.
  functionCallingLlm?: string;
  memoryMode?: ECrewAiAgentMemoryMode;
  // Enable reasoning (chain-of-thought) for this agent. Defaults to false.
  reasoning?: boolean;
}

export type IAiAgentElkNodeData = {
  metadata: ICrewAgent;
  sourceHandles: { id: string }[];
  targetHandles: { id: string }[];
  setNodes: (nodes: any[]) => void;
  className?: string;
  isAnySelected?: boolean;
};

export interface ICrewAgentDTO {
  crewAgents: ICrewAgent[];
  crew_inputs: ICrewInput[];
  crew_custom_data_types?: ICrewCustomDataType[];
  templateId?: string;
  processType?: number;
  managerAgent?: ICrewManagerAgent;
  // Crew-level memory options. Drives the embedder used by *shared* crew memory --
  // i.e. hierarchical mode (manager.memoryMode === Shared) and sequential mode when
  // any sub-agent opts into Shared. Per-agent Isolated stores read their embedder
  // from ICrewAgent.memoryOptions instead. Backend counterpart: CrewInfoModel.MemoryOptions.
  memoryOptions?: ICrewMemoryOptions;
  // Derived at codegen time from managerAgent.memoryMode === Shared.
  // Kept optional on the DTO for backward compatibility with persisted payloads;
  // the frontend no longer sends it and the backend should derive, not trust it.
  /** @deprecated Derived server-side from managerAgent.memoryMode === Shared. */
  memoryEnabled?: boolean;
}

export interface IAgentChatResponseDTO {
  agent_prompt: string;
  assistant_message: string;
  is_agent_ready_for_creation: boolean;
}

export interface IGenerateAgentRequestDTO {
  userMessage: string;
  previousAgent: string;
  sessionId?: string;
  processType?: string;
}

export interface IChatHistoryRequestDTO {
  sessionId: string;
}

export interface IAiAgentModel {
  id: string;
  name: string;
  description: string;
}

export interface IAiAgentTemplate {
  id: string;
  modelId: string;
  name: string;
  description: string;
  shortDescription?: string;
  agentModel: string;
  imageUrl: string;
}

export interface ITestAgentRequest {
  crewAgents: ICrewAgent[];
  crew_inputs: ICrewInput[];
  crew_custom_data_types?: ICrewCustomDataType[];
}

export interface IAgentTaskOutput {
  agent: string;
  expected_output: string;
  output: string;
  task_description: string;
}

export interface ITestAgentResponse {
  json_dict: any;
  pydantic: any;
  raw: string;
  tasks_output: IAgentTaskOutput[];
  token_usage: number;
}
